import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  ComposedChart,
} from 'recharts';
import { UserProgress } from '../../types/workout';
import { WorkoutUtils } from '../../utils/workoutUtils';

interface WorkoutSummaryChartsProps {
  userProgress: UserProgress | null;
}

type ChartType = 'volume-trends' | 'cumulative-volume' | 'exercise-distribution' | 'workout-frequency' | 'duration-trends' | 'rpe-accuracy' | 'consistency-metrics';

const CHART_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
  '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'
];

export const WorkoutSummaryCharts: React.FC<WorkoutSummaryChartsProps> = ({ userProgress }) => {
  const [selectedChart, setSelectedChart] = useState<ChartType>('volume-trends');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChartChange = (event: SelectChangeEvent) => {
    setSelectedChart(event.target.value as ChartType);
  };

  const chartOptions = [
    { value: 'volume-trends', label: 'Volume Trends', description: 'Weekly volume progression over time' },
    { value: 'cumulative-volume', label: 'Cumulative Volume', description: 'Total volume accumulated over time' },
    { value: 'exercise-distribution', label: 'Exercise Distribution', description: 'Volume breakdown by exercise' },
    { value: 'workout-frequency', label: 'Workout Frequency', description: 'Completion rates by day of week' },
    { value: 'duration-trends', label: 'Duration Trends', description: 'Average workout duration by week' },
    { value: 'rpe-accuracy', label: 'RPE Accuracy', description: 'Weekly RPE estimation accuracy' },
    { value: 'consistency-metrics', label: 'Training Consistency', description: 'Consistency and streak metrics' },
  ];

  const selectedOption = chartOptions.find(option => option.value === selectedChart);

  const renderChart = () => {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No workout data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete some workouts to see your summary charts
          </Typography>
        </Box>
      );
    }

    switch (selectedChart) {
      case 'volume-trends':
        const volumeData = WorkoutUtils.getVolumeProgressData(userProgress);
        
        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={volumeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ value: 'Volume (lbs)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => value.toLocaleString()}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    `${value.toLocaleString()} lbs`,
                    name === 'totalVolume' ? 'Total Volume' : 'Average per Workout'
                  ]}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="totalVolume" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  name="totalVolume"
                />
                <Line 
                  type="monotone" 
                  dataKey="averageVolume" 
                  stroke="#82ca9d" 
                  strokeWidth={2}
                  dot={{ fill: '#82ca9d', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  name="averageVolume"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'cumulative-volume':
        const cumulativeData = WorkoutUtils.getVolumeProgressData(userProgress);
        
        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cumulativeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ value: 'Cumulative Volume (lbs)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => value.toLocaleString()}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number) => [`${value.toLocaleString()} lbs`, 'Cumulative Volume']}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Area 
                  type="monotone" 
                  dataKey="cumulativeVolume" 
                  stroke="#8884d8" 
                  fill="#8884d8"
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'exercise-distribution':
        const exerciseData = WorkoutUtils.getExerciseDistributionData(userProgress);
        
        if (exerciseData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No exercise distribution data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete more workouts to see exercise breakdown
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{ 
              width: '100%', 
              height: isMobile ? 250 : 300,
              '& .recharts-wrapper': {
                pointerEvents: 'none !important'
              }
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={exerciseData.slice(0, 8)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => isMobile ? `${percentage}%` : `${name}: ${percentage}%`}
                    outerRadius={isMobile ? 60 : 80}
                    fill="#8884d8"
                    dataKey="totalVolume"
                  >
                    {exerciseData.slice(0, 8).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number, name: string, props: any) => [
                      `${value.toLocaleString()} lbs (${props.payload.percentage}%)`,
                      'Volume'
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            
            {isMobile && (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: 1, 
                width: '100%',
                mt: 2
              }}>
                {exerciseData.slice(0, 8).map((exercise, index) => (
                  <Box key={exercise.exerciseName} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                        borderRadius: 1
                      }} 
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      {exercise.exerciseName.length > 15 
                        ? `${exercise.exerciseName.substring(0, 15)}...` 
                        : exercise.exerciseName
                      }
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 'workout-frequency':
        const frequencyData = WorkoutUtils.getWorkoutFrequencyByDay(userProgress);
        
        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={frequencyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="day" 
                  label={{ value: 'Day of Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? 'end' : 'middle'}
                  height={isMobile ? 60 : 30}
                />
                <YAxis 
                  label={{ value: 'Completion Rate (%)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    `${value}%`,
                    'Completion Rate'
                  ]}
                  labelFormatter={(day) => `${day}`}
                />
                <Bar 
                  dataKey="completionRate" 
                  fill="#8884d8"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'duration-trends':
        const durationData = WorkoutUtils.getWeeklyDurationTrends(userProgress);
        
        if (durationData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No duration data available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete more workouts with duration tracking to see trends
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={durationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="duration"
                  label={{ value: 'Duration (min)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="count"
                  orientation="right"
                  label={{ value: 'Workouts', angle: 90, position: 'insideRight' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    name === 'totalWorkouts' ? value : `${value} min`,
                    name === 'averageDuration' ? 'Average Duration' :
                    name === 'shortestWorkout' ? 'Shortest' :
                    name === 'longestWorkout' ? 'Longest' : 'Workouts'
                  ]}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Bar 
                  yAxisId="count"
                  dataKey="totalWorkouts" 
                  fill="#82ca9d"
                  opacity={0.7}
                  name="totalWorkouts"
                />
                <Line 
                  yAxisId="duration"
                  type="monotone" 
                  dataKey="averageDuration" 
                  stroke="#8884d8" 
                  strokeWidth={3}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: isMobile ? 4 : 6 }}
                  name="averageDuration"
                />
                <Line 
                  yAxisId="duration"
                  type="monotone" 
                  dataKey="shortestWorkout" 
                  stroke="#ffc658" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#ffc658', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  name="shortestWorkout"
                />
                <Line 
                  yAxisId="duration"
                  type="monotone" 
                  dataKey="longestWorkout" 
                  stroke="#ff7c7c" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#ff7c7c', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  name="longestWorkout"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'rpe-accuracy':
        const rpeData = WorkoutUtils.getWeeklyRPEAccuracy(userProgress);
        
        if (rpeData.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No RPE accuracy data available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete workouts with RPE tracking to see accuracy trends
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rpeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="accuracy"
                  domain={[0, 100]}
                  label={{ value: 'Accuracy %', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="rpe"
                  orientation="right"
                  domain={[6, 10]}
                  label={{ value: 'RPE', angle: 90, position: 'insideRight' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    name === 'accuracy' ? `${value}%` : value,
                    name === 'accuracy' ? 'RPE Accuracy' :
                    name === 'averageExpectedRPE' ? 'Expected RPE' :
                    name === 'averageActualRPE' ? 'Actual RPE' : name
                  ]}
                  labelFormatter={(week, payload) => {
                    if (payload && payload[0]) {
                      const data = payload[0].payload;
                      return `Week ${week} (${data.totalSets} sets, ${data.exerciseCount} exercises)`;
                    }
                    return `Week ${week}`;
                  }}
                />
                <Bar 
                  yAxisId="accuracy"
                  dataKey="accuracy" 
                  fill="#8884d8"
                  radius={[4, 4, 0, 0]}
                  name="accuracy"
                />
                <Line 
                  yAxisId="rpe"
                  type="monotone" 
                  dataKey="averageExpectedRPE" 
                  stroke="#82ca9d" 
                  strokeWidth={2}
                  dot={{ fill: '#82ca9d', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  name="averageExpectedRPE"
                />
                <Line 
                  yAxisId="rpe"
                  type="monotone" 
                  dataKey="averageActualRPE" 
                  stroke="#ffc658" 
                  strokeWidth={2}
                  dot={{ fill: '#ffc658', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  name="averageActualRPE"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'consistency-metrics':
        const consistencyData = WorkoutUtils.getTrainingConsistencyMetrics(userProgress);
        const varietyData = WorkoutUtils.getExerciseVarietyMetrics(userProgress);
        
        // Create chart data for consistency visualization
        const consistencyChartData = [
          { metric: 'Weekly\nConsistency', value: consistencyData.weeklyConsistency, color: '#8884d8' },
          { metric: 'Current\nStreak', value: consistencyData.currentStreak, color: '#82ca9d' },
          { metric: 'Longest\nStreak', value: consistencyData.longestStreak, color: '#ffc658' },
          { metric: 'Exercise\nVariety', value: varietyData.totalUniqueExercises, color: '#ff7c7c' },
        ];

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={consistencyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="metric" 
                  label={{ value: 'Training Metrics', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 9 : 12 }}
                  interval={0}
                />
                <YAxis 
                  label={{ value: 'Value', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string, props: any) => [
                    props.payload.metric.includes('Consistency') ? `${value}%` : value,
                    props.payload.metric.replace('\n', ' ')
                  ]}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {consistencyChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'flex-start' }, 
          mb: 3,
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Workout Summary Analysis
            </Typography>
            {selectedOption && (
              <Typography variant="body2" color="text.secondary">
                {selectedOption.description}
              </Typography>
            )}
          </Box>
          
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <Select
              value={selectedChart}
              onChange={handleChartChange}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    zIndex: 1400,
                  },
                },
              }}
            >
              {chartOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {renderChart()}
      </CardContent>
    </Card>
  );
}; 